import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../Education.css';

const Beginner: React.FC = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    }
  };

  const modules = [
    {
      title: 'Introduction to Financial Markets',
      topics: [
        'What are financial markets and how they work',
        'Different types of markets (Forex, Stocks, Commodities, Crypto)',
        'Market participants and their roles',
        'How to open and use a trading account',
        'Understanding market hours and sessions'
      ],
      duration: '2-3 hours'
    },
    {
      title: 'Understanding Trading Basics',
      topics: [
        'What is buying and selling in trading',
        'Understanding long and short positions',
        'How to read price quotes and charts',
        'Market orders vs. Limit orders',
        'Understanding bid, ask, and spread',
        'What is leverage and margin'
      ],
      duration: '3-4 hours'
    },
    {
      title: 'Technical Analysis Fundamentals',
      topics: [
        'Introduction to candlestick charts',
        'Understanding support and resistance',
        'Trend identification (uptrend, downtrend, sideways)',
        'Basic chart patterns (triangles, flags, rectangles)',
        'Introduction to moving averages',
        'Using volume in analysis'
      ],
      duration: '4-5 hours'
    },
    {
      title: 'Risk Management Principles',
      topics: [
        'Why risk management is crucial',
        'Understanding risk-to-reward ratio',
        'Setting stop-loss and take-profit levels',
        'Position sizing and capital allocation',
        'The 1-2% rule for risk per trade',
        'Common beginner mistakes to avoid'
      ],
      duration: '2-3 hours'
    }
  ];

  const keySkills = [
    { skill: 'Chart Reading', description: 'Learn to read and interpret basic price charts' },
    { skill: 'Order Execution', description: 'Understand how to place and manage trades' },
    { skill: 'Basic Analysis', description: 'Identify trends and simple patterns' },
    { skill: 'Risk Control', description: 'Protect your capital with proper risk management' },
    { skill: 'Trading Psychology', description: 'Manage emotions and avoid common pitfalls' },
    { skill: 'Platform Navigation', description: 'Master your trading platform interface' }
  ];

  return (
    <motion.div 
      className="education-page"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div className="page-content">
        <motion.div className="page-title" variants={itemVariants}>
          <h1>Beginner Trading Course</h1>
          <p>Start your trading journey with solid fundamentals</p>
        </motion.div>

        <motion.section className="course-overview" variants={itemVariants}>
          <h2>Course Overview</h2>
          <p>
            This comprehensive beginner course is designed for individuals with little to no trading experience. 
            We'll take you from understanding what financial markets are, to placing your first trades with confidence.
          </p>
          <div className="course-stats">
            <div className="stat">
              <h3>12-15 Hours</h3>
              <p>Total Course Duration</p>
            </div>
            <div className="stat">
              <h3>4 Modules</h3>
              <p>Structured Learning</p>
            </div>
            <div className="stat">
              <h3>Lifetime Access</h3>
              <p>Learn at Your Pace</p>
            </div>
            <div className="stat">
              <h3>Certificate</h3>
              <p>Upon Completion</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="learning-path" variants={itemVariants}>
          <h2>What You'll Learn</h2>
          <div className="modules-list">
            {modules.map((module, index) => (
              <motion.div 
                key={index} 
                className="module-card"
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
              >
                <div className="module-header">
                  <span className="module-number">Module {index + 1}</span>
                  <span className="module-duration">⏱️ {module.duration}</span>
                </div>
                <h3>{module.title}</h3>
                <ul className="topics-list">
                  {module.topics.map((topic, topicIndex) => (
                    <li key={topicIndex}>{topic}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section className="key-skills" variants={itemVariants}>
          <h2>Key Skills You'll Develop</h2>
          <div className="skills-grid">
            {keySkills.map((item, index) => (
              <motion.div 
                key={index} 
                className="skill-card"
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
              >
                <h3>✓ {item.skill}</h3>
                <p>{item.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section className="course-benefits" variants={itemVariants}>
          <h2>Why Start with This Course?</h2>
          <div className="benefits-grid">
            <div className="benefit">
              <h3>🎯 Clear Structure</h3>
              <p>Follow a step-by-step curriculum designed specifically for beginners</p>
            </div>
            <div className="benefit">
              <h3>📚 No Prerequisites</h3>
              <p>No prior knowledge required—we start from absolute basics</p>
            </div>
            <div className="benefit">
              <h3>💡 Practical Examples</h3>
              <p>Real-world examples and case studies to reinforce learning</p>
            </div>
            <div className="benefit">
              <h3>🛡️ Risk-First Approach</h3>
              <p>Learn proper risk management before taking your first trade</p>
            </div>
            <div className="benefit">
              <h3>🎓 Expert Guidance</h3>
              <p>Learn from professional traders with years of experience</p>
            </div>
            <div className="benefit">
              <h3>📱 Flexible Learning</h3>
              <p>Study at your own pace, on any device, anytime</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="next-steps" variants={itemVariants}>
          <h2>After This Course</h2>
          <p>
            Upon completing the beginner course, you'll have a solid foundation in trading basics. 
            You'll be ready to practice on a demo account and gradually transition to the Intermediate course, 
            where you'll learn advanced techniques and develop your own trading strategies.
          </p>
          <div className="progression-path">
            <div className="path-step current">
              <h4>✓ Beginner</h4>
              <p>Build Foundation</p>
            </div>
            <div className="path-arrow">→</div>
            <div className="path-step">
              <h4>Intermediate</h4>
              <p>Develop Skills</p>
            </div>
            <div className="path-arrow">→</div>
            <div className="path-step">
              <h4>Advanced</h4>
              <p>Master Trading</p>
            </div>
          </div>
        </motion.section>

        <motion.section className="important-notes" variants={itemVariants}>
          <h2>⚠️ Important for Beginners</h2>
          <div className="notes-box">
            <ul>
              <li><strong>Start with Demo Trading:</strong> Practice on a demo account before risking real money</li>
              <li><strong>Never Risk More Than You Can Afford to Lose:</strong> Only trade with capital you can afford to lose</li>
              <li><strong>Be Patient:</strong> Learning to trade takes time—don't rush the process</li>
              <li><strong>Keep a Trading Journal:</strong> Document your learning journey and practice trades</li>
              <li><strong>Avoid Get-Rich-Quick Schemes:</strong> Trading requires education, practice, and discipline</li>
              <li><strong>Manage Expectations:</strong> Professional traders focus on consistent, small gains</li>
            </ul>
          </div>
        </motion.section>

        <motion.div className="cta-section" variants={itemVariants}>
          <h2>Ready to Begin Your Trading Journey?</h2>
          <p>Take the first step towards becoming a successful trader</p>
          <div className="btn-container">
            <Link to="/contact" className="btn">Enroll Now</Link>
            <Link to="/education/intermediate" className="btn secondary">View Intermediate Course</Link>
            <Link to="/services/webinar" className="btn secondary">Attend Free Webinar</Link>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Beginner;
